/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { ActionButton, Pivot, PivotItem, Separator, Stack, Text } from "@fluentui/react";
import "../../node_modules/office-ui-fabric-core/dist/css/fabric.min.css";
import ViewHeader from "../MainView/ViewHeader";
import { ISettingGroupDefinition } from "@nuance/hierarchical-settings-ui-domain";
import styles from "./AdvancedView.module.css";
import RulesGrid from "./RulesGrid/RulesGrid";
import { AdvancedViewCommands, IAdvancedViewViewModel } from "./AdvancedViewViewModels";
import { IAdvancedViewState } from "./IAdvancedViewState";
import { IAdvancedViewProps } from "./IAdvancedViewProps";
import EnvironmentSettingView from "./EnvironmentSettings/EnvironmentSettingView";
import StringView from "./StringView/StringView";
import { HuxMessageBar } from "@nuance/hux-components";

const advancedViewCommands = new AdvancedViewCommands();
const cmds = advancedViewCommands.Cmds;

const AdvancedView = (props: IAdvancedViewProps): JSX.Element => {
    if (props.mainFrameVM.state.definition === undefined) {
        throw new Error("Definition should not be undefined here.");
    }

    const initialState: IAdvancedViewState = {
        definition: props.mainFrameVM.state.definition,
        mainFrameVM: props.mainFrameVM
    };

    const [state, dispatch] = React.useReducer(advancedViewCommands.Reducer, initialState);

    const advancedViewVM: IAdvancedViewViewModel = { state, cmds, dispatch };

    const onBackToMainView = (definition: ISettingGroupDefinition) => {
        const mainFrameDispatch = props.mainFrameVM.dispatch;
        const mainFrameCmds = props.mainFrameVM.cmds;
        mainFrameDispatch(mainFrameCmds.openMainView(definition));
    };

    return (
        <>
            <ViewHeader />
            <div className={"ms-Fabric " + styles.rulesMargin} dir="ltr">
                <div className="ms-Grid">
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12">
                            <Stack>
                                <ActionButton
                                    onClick={() =>
                                        onBackToMainView(advancedViewVM.state.definition)
                                    }
                                    iconProps={{ iconName: "ChevronLeft" }}
                                >
                                    Back to Settings Definition
                                </ActionButton>
                                <h2>
                                    <Text variant="xxLarge">Advanced Definition Details</Text>
                                </h2>
                            </Stack>
                        </div>
                    </div>

                    <Separator />
                    <HuxMessageBar pageId={"AdvancedView"}></HuxMessageBar>

                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12 ">
                            <Pivot aria-label="Rules and environment defaults tab strip">
                                <PivotItem headerText="Strings">
                                    <StringView
                                        advancedViewVM={advancedViewVM}
                                        definition={advancedViewVM.state.definition}
                                    />
                                </PivotItem>
                                <PivotItem headerText="Setting rules">
                                    <RulesGrid
                                        advancedViewVM={advancedViewVM}
                                        definition={advancedViewVM.state.definition}
                                    />
                                </PivotItem>

                                <PivotItem headerText="Environment defaults">
                                    <EnvironmentSettingView
                                        advancedViewVM={advancedViewVM}
                                        definition={advancedViewVM.state.definition}
                                    />
                                </PivotItem>
                            </Pivot>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdvancedView;
